// @ts-check
import React, { useCallback, useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Flex,
  Icon,
  Loading,
  P,
  Popover,
  Small,
} from "orcs-design-system";
import { StyledToolCallBubble } from "./StyledComponents";
import { toolCallMessage } from "./prop-types";

/**
 * @typedef {Object} ToolMessageProps
 * @property {import("./types").ToolCallMessage} message
 * @property {import("./types").ToolCallMessage[]} [allToolsCalled]
 *
 */

/**
 * @typedef {Object} ToolCallLabelProps
 * @property {import("./types").ToolCallMessage} message
 */

/**
 * @param {ToolCallLabelProps} props
 * @returns {React.ReactElement}
 */
const ToolCallLabel = ({ message }) => {
  const { complete, content } = message;
  const isRunning = !complete;
  const isCompleted = complete;

  return (
    <Flex flex="1" width="100%">
      <P>{content}</P>

      <Small ml="auto" color="greyDarker" fontWeight="600">
        {isRunning ? "Running..." : isCompleted ? "Completed" : "Ready"}
      </Small>
    </Flex>
  );
};

ToolCallLabel.propTypes = {
  message: toolCallMessage,
};

/**
 * @param {ToolMessageProps} props
 * @returns {React.ReactElement}
 */
export const ToolMessage = ({ message, allToolsCalled = [] }) => {
  const { complete, count } = message;
  const [isExpanded, setIsExpanded] = useState(false);
  const isRunning = !complete;
  const otherToolsCalledCount = count - 1;

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const otherToolsCalledLabel = useMemo(() => {
    if (isExpanded) {
      return "- click to collapse";
    }

    return `+ ${otherToolsCalledCount} more ${
      otherToolsCalledCount > 1 ? "tools" : "tool"
    } used`;
  }, [otherToolsCalledCount, isExpanded]);

  return (
    <Popover
      text={isExpanded ? "click to collapse" : "click to expand"}
      direction="top"
      width="fit-content"
    >
      <StyledToolCallBubble onClick={toggleExpanded}>
        <Box mr="s">
          {isRunning ? (
            <Loading small mr="s" />
          ) : (
            <Icon icon={["fas", "check-circle"]} />
          )}
        </Box>
        <Flex flexDirection="column" gap="xs" flex="1">
          {isExpanded && (
            <Flex flexDirection="column" gap="xs">
              {allToolsCalled.map((toolCall) => (
                <ToolCallLabel key={toolCall.content} message={toolCall} />
              ))}
            </Flex>
          )}

          {!isExpanded && <ToolCallLabel message={message} />}

          {!!otherToolsCalledCount && (
            <Small color="grey">{otherToolsCalledLabel}</Small>
          )}
        </Flex>
      </StyledToolCallBubble>
    </Popover>
  );
};

ToolMessage.propTypes = {
  message: toolCallMessage,
  allToolsCalled: PropTypes.arrayOf(toolCallMessage),
};
